import { Link } from "react-router-dom";
import { ClipboardCheck, FileCheck, Building2, ShieldCheck, ListChecks } from "lucide-react";
import { Section, SectionHeading, PageHero } from "../components/site/section";
import { PageCta } from "../components/site/page-cta";
import { Button } from "../components/ui/button";

const pillars = [
    {
        icon: ClipboardCheck,
        title: "Inspections",
        description: "Track upcoming, failed and closed inspections across DOB, HPD, FDNY, DEP and DOT. Violerts pulls inspection dates, results and required re-inspection windows so you never miss a follow-up.",
        points: [
            "Centralized inspection calendar per property",
            "Automatic re-inspection and correction-deadline alerts",
            "Failed-inspection escalation to your team or expeditor",
        ],
    },
    {
        icon: FileCheck,
        title: "Local Laws",
        description: "NYC's local laws — LL11, LL126, LL152, emissions reporting and more — come with fixed filing windows and stiff penalties. Violerts maps each building's obligations to the current rule set and flags what's due.",
        points: [
            "Rule-to-building obligation mapping",
            "Deadline countdowns and filing-status tracking",
            "Document storage linked to each compliance item",
        ],
    },
    {
        icon: Building2,
        title: "Portfolio Management",
        description: "See every open violation, inspection, permit and deadline across your entire portfolio in one dashboard. Filter by owner, manager, borough or asset class and export reports for lenders, insurers or internal reviews.",
        points: [
            "Portfolio-wide risk scoring and status rollups",
            "Property-level and owner-level views",
            "Exportable compliance reports for stakeholders",
        ],
    },
];
function Compliance() {
    return (<>
      <PageHero title="Inspections, local laws and portfolio management." description="Stay ahead of NYC's moving regulatory targets with a single system that tracks what matters across every building you own or manage."/>

      <Section>
        <SectionHeading title="What Violerts tracks for you"/>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {pillars.map((p) => (<div key={p.title} className="rounded-2xl border border-border bg-card p-8 shadow-card">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-foreground/10 text-foreground">
                <p.icon className="h-6 w-6"/>
              </div>
              <h2 className="mt-6 text-xl font-semibold tracking-tight">{p.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {p.description}
              </p>
              <ul className="mt-6 space-y-3">
                {p.points.map((pt) => (<li key={pt} className="flex items-start gap-3 text-sm">
                    <ListChecks className="mt-0.5 h-4 w-4 shrink-0 text-foreground"/>
                    <span>{pt}</span>
                  </li>))}
              </ul>
            </div>))}
        </div>
      </Section>

      <Section className="surface-ink">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-signal/10 text-signal">
            <ShieldCheck className="h-6 w-6"/>
          </div>
          <h2 className="mt-6 text-3xl font-semibold tracking-tight sm:text-4xl">
            Compliance is more than a checklist
          </h2>
          <p className="mt-4 text-lg opacity-80">
            It's a constantly changing set of deadlines, agencies and records. Violerts automates the watching so your team can focus on fixing issues before they become fines.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild variant="signal" size="lg" className="rounded-full">
              <Link to="/contact">Talk to our team</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-full">
              <Link to="/reports" className="text-black hover:text-black">View reports</Link>
            </Button>
          </div>
        </div>
      </Section>

      <Section>
        <SectionHeading title="How compliance tracking works"/>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { step: "1", title: "Connect properties", body: "Add addresses or portfolios in minutes." },
            { step: "2", title: "Monitor agencies", body: "Violerts watches DOB, HPD, ECB, FDNY, DEP, DOT and more." },
            { step: "3", title: "Get alerted", body: "New violations, inspections and deadlines reach you when Violerts detects new or updated records." },
            { step: "4", title: "Close out", body: "Track cure steps, filings and certifications until each item is resolved." },
        ].map((s) => (<div key={s.step} className="rounded-xl border border-border bg-card p-6 shadow-card">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-signal/10 text-sm font-semibold text-foreground">
                {s.step}
              </span>
              <h3 className="mt-4 font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
            </div>))}
        </div>
      </Section>
      <PageCta />
    </>);
}

export default Compliance;
